import { useMemo, type ReactNode } from 'react';

type SimpleMarkdownProps = {
  text: string;
};

type Block =
  | { type: 'h1' | 'h2' | 'h3' | 'p' | 'blockquote'; content: string }
  | { type: 'ul' | 'ol'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'br' | 'hr' };

function renderInline(text: string): ReactNode[] {
  const brSegments = text.split(/::br/g);
  if (brSegments.length > 1) {
    const result: ReactNode[] = [];
    brSegments.forEach((segment, i) => {
      if (segment) result.push(...renderInlineFormatting(segment));
      if (i < brSegments.length - 1) result.push(<br key={`br-${i}`} />);
    });
    return result;
  }
  return renderInlineFormatting(text);
}

function renderInlineFormatting(text: string): ReactNode[] {
  // Process bold (**text** or __text__), italic (*text* or _text_), and strikethrough (~~text~~)
  const parts: ReactNode[] = [];
  const re = /`([^`]+)`|(\*\*|__)(.+?)\2|(~~)(.+?)\4|(\*|_)(.+?)\6|<u>(.+?)<\/u>|\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      // inline code
      parts.push(<code key={match.index}>{match[1]}</code>);
    } else if (match[2]) {
      // bold
      parts.push(<strong key={match.index}>{match[3]}</strong>);
    } else if (match[4]) {
      // strikethrough
      parts.push(<del key={match.index}>{match[5]}</del>);
    } else if (match[6]) {
      // italic
      parts.push(<em key={match.index}>{match[7]}</em>);
    } else if (match[8]) {
      // underline
      parts.push(<u key={match.index}>{match[8]}</u>);
    } else if (match[9]) {
      // link [text](url)
      parts.push(<a key={match.index} href={match[10]} target="_blank" rel="noopener noreferrer">{match[9]}</a>);
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let currentParagraph = '';
  let currentList: { type: 'ul' | 'ol'; items: string[] } | null = null;

  let currentTable: { headers: string[]; rows: string[][] } | null = null;

  const flushParagraph = () => {
    const trimmed = currentParagraph.trim();
    if (trimmed) blocks.push({ type: 'p', content: trimmed });
    currentParagraph = '';
  };

  const flushList = () => {
    if (currentList && currentList.items.length > 0) {
      blocks.push(currentList);
    }
    currentList = null;
  };

  const flushTable = () => {
    if (currentTable && currentTable.headers.length > 0) {
      blocks.push({ type: 'table', ...currentTable });
    }
    currentTable = null;
  };

  const parseTableRow = (line: string): string[] =>
    line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

  const isTableSeparator = (line: string): boolean =>
    /^\|?(\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?$/.test(line.trim());

  // Normalize: ensure every heading marker starts on its own line.
  const normalized = text.replace(/\r\n/g, '\n').replace(/([^#\n])(#{1,3}\s)/g, '$1\n$2');
  const lines = normalized.split('\n');

  for (const line of lines) {
    if (line.trim() === '::br') {
      flushParagraph();
      flushList();
      flushTable();
      blocks.push({ type: 'br' });
      continue;
    }

    if (line.trim() === '::hr' || /^-{3,}$/.test(line.trim()) || /^\*{3,}$/.test(line.trim())) {
      flushParagraph();
      flushList();
      flushTable();
      blocks.push({ type: 'hr' });
      continue;
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.*)/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      flushTable();
      const level = headingMatch[1].length as 1 | 2 | 3;
      const type = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
      const content = headingMatch[2].trim();
      if (content) blocks.push({ type, content });
      continue;
    }

    if (line.trim().includes('|') && /\|/.test(line)) {
      if (isTableSeparator(line)) {
        continue;
      }
      const cells = parseTableRow(line);
      if (currentTable) {
        currentTable.rows.push(cells);
      } else {
        flushParagraph();
        flushList();
        currentTable = { headers: cells, rows: [] };
      }
      continue;
    }

    // If we were collecting a table, flush it
    if (currentTable) {
      flushTable();
    }

    // Blockquote: > text
    const bqMatch = line.match(/^>\s?(.*)/);
    if (bqMatch) {
      flushList();
      flushTable();
      const prev = blocks.length > 0 ? blocks[blocks.length - 1] : null;
      if (currentParagraph === '' && prev && prev.type === 'blockquote') {
        prev.content += ' ' + bqMatch[1].trim();
      } else {
        flushParagraph();
        blocks.push({ type: 'blockquote', content: bqMatch[1].trim() });
      }
      continue;
    }

    // Unordered list item: - item, * item, + item
    const ulMatch = line.match(/^[\t ]*[-*+]\s+(.*)/);
    if (ulMatch) {
      flushParagraph();
      if (!currentList || currentList.type !== 'ul') {
        flushList();
        currentList = { type: 'ul', items: [] };
      }
      currentList.items.push(ulMatch[1].trim());
      continue;
    }

    // Ordered list item: 1. item, 2) item
    const olMatch = line.match(/^[\t ]*\d+[.)]\s+(.*)/);
    if (olMatch) {
      flushParagraph();
      if (!currentList || currentList.type !== 'ol') {
        flushList();
        currentList = { type: 'ol', items: [] };
      }
      currentList.items.push(olMatch[1].trim());
      continue;
    }

    if (line.trim() === '') {
      flushParagraph();
      flushList();
      flushTable();
    } else {
      flushList();
      currentParagraph += (currentParagraph ? ' ' : '') + line;
    }
  }

  flushParagraph();
  flushList();
  flushTable();
  return blocks;
}

export default function SimpleMarkdown({ text }: SimpleMarkdownProps) {
  const blocks = useMemo(() => parseBlocks(text), [text]);

  return (
    <>
      {blocks.map((block, i) => {
        if (block.type === 'br') {
          return <br key={i} />;
        }
        if (block.type === 'hr') {
          return <hr key={i} />;
        }
        if (block.type === 'blockquote') {
          return <blockquote key={i}>{renderInline(block.content)}</blockquote>;
        }
        if (block.type === 'table') {
          return (
            <table key={i} className="simple-md-table">
              <thead>
                <tr>
                  {block.headers.map((h, j) => <th key={j}>{renderInline(h)}</th>)}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => <td key={ci}>{renderInline(cell)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
        if (block.type === 'ul') {
          return (
            <ul key={i}>
              {block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}
            </ul>
          );
        }
        if (block.type === 'ol') {
          return (
            <ol key={i}>
              {block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}
            </ol>
          );
        }
        const children = renderInline((block as { type: string; content: string }).content);
        switch (block.type) {
          case 'h1': return <h1 key={i}>{children}</h1>;
          case 'h2': return <h2 key={i}>{children}</h2>;
          case 'h3': return <h3 key={i}>{children}</h3>;
          default: return <p key={i}>{children}</p>;
        }
      })}
    </>
  );
}
